import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  provideRouter,
  RedirectCommand,
  Router,
  withComponentInputBinding,
  withNavigationErrorHandler,
} from '@angular/router';

import { authInterceptor } from './api/auth-interceptor';
import { apiBaseUrlInterceptor } from './api/base-url-interceptor';
import { sessionExpiredInterceptor } from './api/session-expired-interceptor';
import { AppRoot } from './app/app-root/app-root';
import { standaloneRoutes } from './app/standalone.routes';
import { provideImaTranslations } from './i18n/providers';
import { StandaloneLocale } from './i18n/standalone-locale';
import { AuthService } from './shell/auth/auth.service';
import { ShellNavigation } from './shell/navigation/shell-navigation';
import { ENABLE_STANDALONE_NAVIGATION } from './shell/navigation/standalone-navigation';
import { RUNTIME_CONFIG, STANDALONE_RUNTIME_CONFIG } from './shell/runtime/config';
import { RuntimeErrorHandler } from './shell/runtime/error-handler';
import { MFE_HOST_ELEMENT, RuntimeFailureReporter } from './shell/runtime/failure-reporter';
import { renderFatalBootstrapError } from './shell/runtime/render-fatal-bootstrap-error';
import { loadStandaloneRuntimeConfig } from './shell/runtime/standalone-runtime-config';
import './app/global-styles';

if (__ENABLE_MSW_MOCKS__) {
  const { startMockWorker } = await import('./mocks/browser');

  await startMockWorker();
}

/*
 * Standalone entry point — used by both the dev server (`npm start` /
 * `npm run local`) and the `build-standalone` production build (see
 * vite.config.ts's BUILD_TARGET switch).
 *
 * This is NOT the entry federation exposes to the Shell — that is
 * src/entrypoints/remote-entry.ts, mounting <angular-profile-widget> with a
 * Shell-owned MemoryLocationStrategy (see app.config.ts /
 * src/entrypoints/remote-bootstrap.ts). Nothing here changes what the
 * Shell loads or how it behaves.
 *
 * This entry instead runs the exact same routes/components through a
 * plain bootstrapApplication() with Angular's default, browser-URL-based
 * PathLocationStrategy: Angular alone owns browser URL/history, initial
 * navigation, and route matching, with no Shell involved anywhere.
 *
 * Runtime configuration (API base URLs, i18n base URL, catalogue revision)
 * is fetched from /runtime-config.json before bootstrapping (see
 * loadStandaloneRuntimeConfig) rather than read off a Shell-supplied
 * attribute — the same build artifact works in local dev (a checked-in
 * public/runtime-config.json ships the dev defaults) and any deployed
 * environment (which serves its own copy of that file, no rebuild needed).
 */
try {
  const runtimeConfig = await loadStandaloneRuntimeConfig();

  await bootstrapApplication(AppRoot, {
    providers: [
      {
        provide: ErrorHandler,
        useClass: RuntimeErrorHandler,
      },
      RuntimeFailureReporter,
      // No real Shell host element exists in this mode; document.body is a
      // harmless dispatch target — nothing outside this page listens
      // (other than this same MFE's own FatalErrorListener).
      { provide: MFE_HOST_ELEMENT, useValue: document.body },
      { provide: RUNTIME_CONFIG, useValue: runtimeConfig },
      { provide: STANDALONE_RUNTIME_CONFIG, useValue: runtimeConfig },
      ...provideImaTranslations(),
      provideAppInitializer(() => inject(StandaloneLocale).initialize()),
      provideHttpClient(
        withInterceptors([authInterceptor, apiBaseUrlInterceptor, sessionExpiredInterceptor]),
      ),
      // Must resolve before the Router's initial navigation runs (which
      // provideAppInitializer blocks on) so authGuard/roleGuard see an
      // accurate signal on the very first navigation.
      provideAppInitializer(() => inject(AuthService).initialize()),
      {
        provide: ENABLE_STANDALONE_NAVIGATION,
        useValue: true,
      },
      provideRouter(
        standaloneRoutes,
        withComponentInputBinding(),
        // A thrown guard/resolver would otherwise leave a half-navigated
        // blank page; send the user home instead of surfacing a raw error.
        withNavigationErrorHandler((error) => {
          console.error('[Angular Profile MFE] Navigation failed.', error);

          return new RedirectCommand(inject(Router).parseUrl('/en/ima'));
        }),
      ),
      ShellNavigation,
      // Deliberately no LocationStrategy override: Angular's default
      // PathLocationStrategy reads/writes the real browser URL/history.
    ],
  });
} catch (error) {
  renderFatalBootstrapError(document.body, error);
}
